import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTabletMode } from '../context/TabletModeContext';
import { ChevronLeft, Check, Plus, X, UtensilsCrossed, Sparkles, AlertCircle, Star, BookOpen, Sun, Moon, Wine, GlassWater } from 'lucide-react';
import ProductCarousel from './ProductCarousel';
import { categoryImages, getProductImage } from '../hooks/useCategoryData';
import { optimizedImageUrl, lazyImgProps } from '../lib/imageOptim';
import { useLanguage } from '../context/LanguageContext';
import { tr } from '../lib/guidedFlowI18n';
import { ScrollArea } from './ui/scroll-area';
import { useSelection } from '../context/SelectionContext';
import {
  T, S, applyTheme, buildS,
  ALCOHOL_CATS, NO_ALCOHOL_CATS, FOOD_CATS,
  catLabel, isCafeOpen, sortProducts, getAutoRecommendations,
} from './GuidedFlow/utils';
import ProductDetailOverlay from './GuidedFlow/ProductDetailOverlay';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────── Step wrapper ─────────── */
const StepWrap = ({ children, stepKey, wide }) => {
  const base = wide ? { ...S.screenInner, maxWidth: '880px' } : S.screenInner;
  // welcome has no TopBar — collapse the top padding reserved for it
  let inner = stepKey === 'welcome' ? { ...base, paddingTop: '24px' } : base;
  // Carousel steps need a bit more breathing room at the bottom so the
  // "Ver todos los X" button doesn't sit glued to the mobile browser UI.
  const isCarouselStep = stepKey === 'drink_rec' || stepKey === 'food_rec'
    || stepKey === 'drink_select' || stepKey === 'food_select';
  if (isCarouselStep) {
    // Portrait viewports (tablet/phone vertical) have plenty of vertical room,
    // so centering looks balanced. Landscape (iPad horizontal) is tight: use
    // flex-start so the price/add button never falls below the fold.
    const portrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;
    inner = {
      ...inner,
      paddingTop: '88px', paddingBottom: '24px',
      justifyContent: portrait ? 'safe center' : 'flex-start',
    };
  }
  // Category banner steps (long scroll on tablet) — pin to top with extra
  // breathing room below the TopBar so the first row doesn't sit glued to it.
  const isCatStep = stepKey === 'drink_cat' || stepKey === 'food_cat';
  if (isCatStep) {
    inner = {
      ...inner,
      paddingTop: 'calc(env(safe-area-inset-top, 0px) + 110px)',
      justifyContent: 'flex-start',
    };
  }
  // Long scrollable steps (stacked banners + summary screens with many items)
  // need an extra in-flow spacer to clear the mobile browser UI / Emergent badge.
  const needsBottomSpacer = stepKey === 'drink_cat' || stepKey === 'food_cat'
    || stepKey === 'summary' || stepKey === 'add_more_choice';
  return (
    <div key={stepKey} style={S.screen}>
      <div style={inner}>
        {children}
        {needsBottomSpacer && (
          <div aria-hidden="true" style={{ height: '100px', flexShrink: 0 }} />
        )}
      </div>
    </div>
  );
};

/* ─────────── Centered content block ─────────── */
const CenteredBlock = ({ children, style }) => (
  <div style={{ maxWidth: '480px', margin: '0 auto', width: '100%', textAlign: 'center', ...style }}>
    {children}
  </div>
);

/* ─────────── Waiter ticket line ─────────── */
const WaiterLine = ({ item, loc, locES, language, T, isLast }) => {
  const displayName = loc(item.name);
  const esName = locES(item.name);
  const showES = language !== 'es' && language !== 'cat' && esName && esName !== displayName;
  const opts = item.selectedOptions || [];
  const optsDisplay = opts.map(o => loc(o.name)).join(', ');
  const optsES = opts.map(o => locES(o.name)).join(', ');
  const showOptsES = showES && optsES && optsES !== optsDisplay;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      gap: '10px', padding: '14px 0',
      borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: 0 }}>
        <span style={{
          fontFamily: T.font.h, fontSize: 'clamp(32px, 7vw, 54px)', color: T.gold, fontWeight: 700,
          minWidth: 'clamp(48px, 10vw, 78px)', flexShrink: 0, lineHeight: 1, letterSpacing: '-0.02em',
        }}>
          ×{item.qty || 1}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontFamily: T.font.b, fontSize: 'clamp(18px, 4.2vw, 33px)', fontWeight: 600, color: T.text, display: 'block', lineHeight: 1.25, wordBreak: 'break-word' }}>
            {displayName}
          </span>
          {showES && (
            <span style={{ fontFamily: T.font.b, fontSize: 'clamp(13px, 2.6vw, 20px)', color: '#ffd97a', fontWeight: 500, display: 'block', marginTop: '4px', lineHeight: 1.3 }}>
              {esName}
            </span>
          )}
          {optsDisplay && (
            <span style={{ fontFamily: T.font.b, fontSize: 'clamp(12px, 2.2vw, 16px)', color: T.textSec, display: 'block', marginTop: '6px' }}>
              {optsDisplay}
            </span>
          )}
          {showOptsES && (
            <span style={{ fontFamily: T.font.b, fontSize: 'clamp(11px, 2vw, 15px)', color: '#ffd97a', display: 'block', marginTop: '2px' }}>
              {optsES}
            </span>
          )}
        </div>
      </div>
      <span style={{ fontFamily: T.font.h, fontSize: 'clamp(18px, 4.2vw, 33px)', color: T.gold, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
        {(item.price * (item.qty || 1)).toFixed(2)}€
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════ */
/*                    PRODUCT DETAIL OVERLAY                          */
/* ═══════════════════════════════════════════════════════════════════ */


/* ═══════════════════════════════════════════════════════════════════ */
/*                          MAIN COMPONENT                           */
/* ═══════════════════════════════════════════════════════════════════ */
/* ─────────── Error Boundary ─────────── */
class GuidedFlowErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[GuidedFlow] Caught error:', error, info); }
  handleReset = () => { this.setState({ hasError: false, error: null }); window.location.reload(); };
  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="guided-flow-error" style={{
          width: '100%', height: '100dvh',
          background: T.bg, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center',
        }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', marginBottom: '24px',
            background: `${T.gold}15`, border: `1px solid ${T.gold}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertCircle size={28} color={T.gold} />
          </div>
          <h2 style={{ fontFamily: T.font.h, fontSize: '24px', fontWeight: 500, color: T.text, marginBottom: '12px' }}>
            Un momento&hellip;
          </h2>
          <p style={{ fontFamily: T.font.b, fontSize: '15px', color: T.textSec, lineHeight: 1.5, marginBottom: '32px', maxWidth: '300px' }}>
            Hemos tenido un pequeno tropiezo. Toca para volver a empezar.
          </p>
          <button onClick={this.handleReset} data-testid="guided-flow-error-retry" style={{ ...S.goldBtn, maxWidth: '280px' }}>
            Volver a empezar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const GuidedFlowInner = () => {
  const { tableNumber } = useParams();
  const navigate = useNavigate();
  const { language: lang, localize } = useLanguage();
  const { isTablet } = useTabletMode();
  const language = lang || 'es';

  /* ── Sun Mode (bright sunlight legibility) ── */
  const [sunMode, setSunMode] = useState(() => {
    try { return localStorage.getItem('pont10_sun_mode') === '1'; }
    catch { return false; }
  });
  // Apply palette synchronously BEFORE this render produces JSX so all
  // child components & helpers read the up-to-date T/S values.
  applyTheme(sunMode ? 'sun' : 'dark');
  const toggleSunMode = useCallback(() => {
    setSunMode(prev => {
      const next = !prev;
      try { localStorage.setItem('pont10_sun_mode', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  const exitToMenu = useCallback(() => {
    sessionStorage.setItem('pont10_entry_choice', 'menu');
    navigate(tableNumber ? `/mesa/${tableNumber}` : '/menu');
  }, [navigate, tableNumber]);

  /* ── Data ── */
  const [allProducts, setAllProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [flowConfig, setFlowConfig] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/products?limit=500`).then(r => r.json()),
      fetch(`${API}/api/categories`).then(r => r.json()),
      fetch(`${API}/api/guided-flow/config`).then(r => r.json()),
    ]).then(([prods, cats, cfg]) => {
      const p = Array.isArray(prods) ? prods : prods.data || [];
      setAllProducts(p.filter(x => x.is_active !== false));
      setAllCategories(Array.isArray(cats) ? cats : []);
      setFlowConfig(cfg);
      setDataLoaded(true);
    }).catch(console.error);
  }, []);

  /* ── Flow state ── */
  // If the user explicitly chose "Que te guiemos" from the entry choice screen
  // there's no need to show the welcome step again — jump straight to party size.
  const skipWelcome = useRef(
    typeof sessionStorage !== 'undefined'
    && sessionStorage.getItem('pont10_entry_choice') === 'guided'
  ).current;
  const [step, setStep] = useState(skipWelcome ? 'party_size' : 'welcome');
  const [partySize, setPartySize] = useState(0);
  const [drinkType, setDrinkType] = useState(null);
  const [drinkCat, setDrinkCat] = useState(null);
  const [foodCat, setFoodCat] = useState(null);
  const [tempSelection, setTempSelection] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailContext, setDetailContext] = useState(null); // 'drink' or 'food'
  const [upsellData, setUpsellData] = useState(null); // current active upsell moment
  const [prevStepBeforeUpsell, setPrevStepBeforeUpsell] = useState(null);
  const [showWaiterModal, setShowWaiterModal] = useState(false);

  /* ── Unified cart sync (shared with Carta Libre via SelectionContext) ── */
  const { items: cartItems, replaceItems: replaceCartItems } = useSelection();

  // Map a cart item (Selection format) back into the local drink/food shape
  // expected by the rest of the flow (`{...product, selectedOptions, qty}`).
  const cartToLocal = useCallback((ci) => ({
    id: ci.id,
    name: ci.name,
    price: ci.price,
    image_url: ci.image,
    images: ci.image ? [ci.image] : [],
    category_id: ci.categoryId,
    selectedOptions: ci.selectedOptions || [],
    qty: ci.quantity || 1,
  }), []);

  const DRINK_CAT_IDS = useMemo(
    () => new Set([...ALCOHOL_CATS, ...NO_ALCOHOL_CATS].map(c => c.id)),
    []
  );
  const FOOD_CAT_IDS = useMemo(() => new Set(FOOD_CATS.map(c => c.id)), []);
  const isDrinkCat = useCallback((cid) => DRINK_CAT_IDS.has(cid), [DRINK_CAT_IDS]);
  const isFoodCat = useCallback((cid) => FOOD_CAT_IDS.has(cid), [FOOD_CAT_IDS]);

  // Derive drink/food lists from the shared cart so anything added in
  // Carta Libre shows up automatically when the user returns to the Guided Flow.
  const selectedDrinks = useMemo(
    () => cartItems.filter(ci => isDrinkCat(ci.categoryId)).map(cartToLocal),
    [cartItems, isDrinkCat, cartToLocal]
  );
  const selectedFood = useMemo(
    () => cartItems.filter(ci => isFoodCat(ci.categoryId)).map(cartToLocal),
    [cartItems, isFoodCat, cartToLocal]
  );

  // Map a local drink/food item back into the SelectionContext cart shape.
  const localToCart = useCallback((it) => ({
    id: it.id,
    name: it.name,
    price: it.price,
    image: it.image_url || (it.images && it.images[0]) || '',
    categoryId: it.category_id,
    isShopItem: false,
    quantity: it.qty || 1,
    selectedOptions: it.selectedOptions || [],
    addedAt: Date.now(),
  }), []);

  // Replace drinks and/or food slices of the cart while preserving any items
  // outside those two domains (shop items, other categories from Carta Libre).
  const setSelectedDrinks = useCallback((newDrinks) => {
    const food = cartItems.filter(ci => isFoodCat(ci.categoryId));
    const others = cartItems.filter(ci => !isDrinkCat(ci.categoryId) && !isFoodCat(ci.categoryId));
    replaceCartItems([...newDrinks.map(localToCart), ...others, ...food]);
  }, [cartItems, isDrinkCat, isFoodCat, replaceCartItems, localToCart]);

  const setSelectedFood = useCallback((newFood) => {
    const drinks = cartItems.filter(ci => isDrinkCat(ci.categoryId));
    const others = cartItems.filter(ci => !isDrinkCat(ci.categoryId) && !isFoodCat(ci.categoryId));
    replaceCartItems([...drinks, ...others, ...newFood.map(localToCart)]);
  }, [cartItems, isDrinkCat, isFoodCat, replaceCartItems, localToCart]);

  /* ── Helpers ── */
  const loc = (name) => localize(name);
  // Spanish copy of a multi-language field, used as a cheat-sheet for the waiter
  // when the customer is browsing in another language.
  const locES = (name) => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    return name.es || name.cat || name.en || name.fr || '';
  };
  const productsInCat = useCallback((catId) => {
    const raw = allProducts.filter(p => p.category_id === catId);
    return sortProducts(raw, flowConfig?.product_sort);
  }, [allProducts, flowConfig?.product_sort]);

  const getRecForCat = useCallback((catId) => {
    const rec = flowConfig?.recommendations?.find(r => r.category_id === catId && r.enabled !== false);
    if (!rec || !rec.product_ids?.length) return null;
    const recProducts = rec.product_ids.map(pid => allProducts.find(p => p.id === pid)).filter(Boolean);
    return recProducts.length > 0 ? { ...rec, products: recProducts } : null;
  }, [flowConfig, allProducts]);

  // Get configurable copy for a step (falls back to defaults)
  const getCopy = useCallback((stepKey, defaultTitle, defaultSub) => {
    // Only use admin custom copy when displaying in Spanish; for other languages
    // fall back to the localized defaults so the UI stays translated.
    const custom = language === 'es'
      ? flowConfig?.step_copy?.find(s => s.step === stepKey)
      : null;
    return {
      title: custom?.title || defaultTitle,
      subtitle: custom?.subtitle || defaultSub,
    };
  }, [flowConfig, language]);

  // Get upsell moment for a trigger step
  const getUpsellFor = useCallback((triggerStep) => {
    return (flowConfig?.upsell_moments || []).find(
      u => u.trigger_after_step === triggerStep && u.enabled
    );
  }, [flowConfig]);

  // Get cross-sell for a category
  const getCrossSellFor = useCallback((catId) => {
    return (flowConfig?.cross_sells || []).find(
      r => r.trigger_category === catId && r.enabled
    );
  }, [flowConfig]);

  // Resolve a category's hero image. Priority:
  //   1. Admin-configured `image` field from /api/categories (same source as
  //      Carta Libre / CategoryView).
  //   2. Hardcoded Unsplash fallback from categoryImages map.
  //   3. Generic `cat-picar` fallback.
  // This keeps the imagery consistent across Modo Carta and Modo Camarero.
  const catImage = useCallback((catId) => {
    const adminCat = allCategories.find(c => c.id === catId);
    return adminCat?.image || categoryImages[catId] || categoryImages['cat-picar'];
  }, [allCategories]);

  // Social proof labels
  const socialProof = flowConfig?.social_proof?.enabled !== false ? flowConfig?.social_proof : null;

  const goBack = () => {
    // If we skipped welcome, "Atrás" from party_size should leave the guided flow.
    if (step === 'party_size' && skipWelcome) {
      exitToMenu();
      return;
    }
    const backMap = {
      party_size: 'welcome',
      drink_type: 'party_size',
      drink_cat: 'drink_type', drink_rec: 'drink_cat', drink_select: 'drink_cat',
      drink_confirm: 'drink_select',
      upsell: prevStepBeforeUpsell || 'drink_confirm',
      food_prompt: 'drink_confirm',
      food_cat: 'drink_confirm', food_rec: 'food_cat', food_select: 'food_cat',
      food_confirm: 'food_select', summary: 'food_prompt',
      add_more_choice: 'summary',
    };
    setStep(backMap[step] || 'welcome');
  };

  // Navigate to next step, checking for upsell moments
  const goToWithUpsell = useCallback((nextStep, fromStep) => {
    const upsell = getUpsellFor(fromStep);
    if (upsell) {
      setUpsellData(upsell);
      setPrevStepBeforeUpsell(fromStep);
      setStep('upsell');
    } else {
      setStep(nextStep);
    }
  }, [getUpsellFor]);

  /* ── Product detail handlers ── */
  const openProductDetail = useCallback((product, context) => {
    setDetailProduct(product);
    setDetailContext(context);
  }, []);

  const handleDetailAdd = useCallback((product, options) => {
    const item = { ...product, qty: 1, selectedOptions: options };
    if (detailContext === 'drink') {
      setTempSelection(item);
      setDetailProduct(null);
      setStep('drink_confirm');
    } else {
      setTempSelection(item);
      setDetailProduct(null);
      setStep('food_confirm');
    }
  }, [detailContext]);

  /* ── Top bar ── */
  const TopBar = () => step !== 'welcome' ? (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      // Respect iOS safe-area (status bar / Dynamic Island) and give tablet
      // a little extra breathing room below the iPad clock/battery.
      paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isTablet ? '20px' : '12px'})`,
      paddingBottom: '8px',
      paddingLeft: '24px', paddingRight: '24px',
      background: `linear-gradient(to bottom, ${T.bg} 70%, transparent)`,
      pointerEvents: 'none',
    }}>
      <button onClick={goBack} data-testid="flow-back-btn" style={{
        background: 'none', border: 'none', color: T.gold, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '4px', fontFamily: T.font.b, fontSize: '15px',
        pointerEvents: 'auto',
      }}>
        <ChevronLeft size={20} /> {tr(language, 'back')}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', pointerEvents: 'auto' }}>
        <button onClick={toggleSunMode} data-testid="flow-sun-mode-btn"
          title={sunMode ? 'Modo noche' : 'Modo Sol — pantalla clara para terraza'}
          aria-label={sunMode ? 'Modo noche' : 'Modo Sol'}
          style={{
            background: sunMode ? T.gold : 'rgba(255,255,255,0.06)',
            border: `1px solid ${T.gold}${sunMode ? '' : '40'}`,
            color: sunMode ? T.bg : T.gold, cursor: 'pointer',
            padding: '14px 22px', borderRadius: '999px',
            display: 'flex', alignItems: 'center', gap: '10px', fontFamily: T.font.b, fontSize: '18px',
            fontWeight: 600,
          }}>
          {sunMode ? <Moon size={22} /> : <Sun size={22} />}
          <span>{sunMode ? 'Noche' : 'Sol'}</span>
        </button>
        <button onClick={exitToMenu} data-testid="flow-exit-btn"
          title="Salir y ver la carta clásica"
          style={{
            background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.gold}40`,
            color: T.gold, cursor: 'pointer', padding: '14px 22px', borderRadius: '999px',
            display: 'flex', alignItems: 'center', gap: '10px', fontFamily: T.font.b, fontSize: '18px',
            fontWeight: 600,
          }}>
          <BookOpen size={22} />
          <span>Carta</span>
        </button>
      </div>
    </div>
  ) : null;

  /* ═══════ STEP: WELCOME ═══════ */
  const WelcomeStep = () => {
    // For non-Spanish languages, ignore admin Spanish-only custom welcome text.
    const wTitle = (language === 'es' && flowConfig?.welcome_title) || tr(language, 'welcome_title_default');
    const wSub = (language === 'es' && flowConfig?.welcome_subtitle) || tr(language, 'welcome_subtitle_default');
    return (
    <StepWrap stepKey="welcome" wide>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '0 8px', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: '-24px', zIndex: -1 }}>
          <img src="https://images.unsplash.com/photo-1633737320463-6daf601b55a5?w=800&q=60" alt=""
            {...lazyImgProps}
            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.25 }} />
          <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at center, ${T.bg}cc 0%, ${T.bg} 75%)` }} />
        </div>

        <p style={{ fontFamily: T.font.b, fontSize: 'clamp(12px, 1.4vw, 15px)', fontWeight: 500, letterSpacing: '0.25em',
          textTransform: 'uppercase', color: T.gold, marginBottom: '28px' }}>
          Pont 10 &middot; Besalú
        </p>
        <h1 style={{ fontFamily: T.font.h, fontSize: 'clamp(38px, 7vw, 68px)', fontWeight: 500, color: T.text,
          lineHeight: 1.1, marginBottom: '20px', letterSpacing: '-0.025em', whiteSpace: 'pre-line', maxWidth: '720px' }}>
          {wTitle}
        </h1>
        <p style={{ fontFamily: T.font.b, fontSize: 'clamp(16px, 2.2vw, 22px)', color: T.textSec, lineHeight: 1.55, marginBottom: '48px', maxWidth: '480px' }}>
          {wSub}
        </p>

        <button onClick={() => setStep('party_size')} data-testid="flow-start-btn"
          style={{ ...S.goldBtn, maxWidth: '340px', padding: '20px' }}>
          {tr(language, 'start')}
        </button>

        {/* Exit link on welcome screen */}
        <button onClick={exitToMenu} data-testid="welcome-exit-btn" style={{
          background: 'none', border: 'none', color: T.textSec, cursor: 'pointer',
          marginTop: '20px', padding: '8px 16px', fontFamily: T.font.b, fontSize: '14px',
          textDecoration: 'underline', textUnderlineOffset: '4px',
        }}>
          {language === 'es' ? 'Ver la carta clásica' : language === 'en' ? 'Browse the menu instead' : language === 'cat' ? 'Veure la carta clàssica' : 'Voir la carte classique'}
        </button>
      </div>
    </StepWrap>
    );
  };

  /* ═══════ STEP: PARTY SIZE ═══════ */
  const PartySizeStep = () => {
    const copy = getCopy('party_size', tr(language, 'party_title'), tr(language, 'party_subtitle'));
    return (
    <StepWrap stepKey="party_size">
      <TopBar />
      <div>
        <CenteredBlock>
          <p style={{ ...S.question, textAlign: 'center', marginBottom: '48px' }}>{copy.title}</p>
        </CenteredBlock>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', maxWidth: '560px', margin: '0 auto' }}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
            <button key={n} data-testid={`party-size-btn-${n}`}
              onClick={() => { setPartySize(n); setStep('drink_type'); }}
              style={{
                aspectRatio: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                borderRadius: '20px', border: `1px solid ${partySize === n ? T.gold : T.border}`,
                background: partySize === n ? '#2A2622' : T.bgEl, cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              <span style={{ fontFamily: T.font.h, fontSize: 'clamp(34px, 4.5vw, 48px)', fontWeight: 300, color: partySize === n ? T.gold : T.text }}>
                {n}
              </span>
              {n === 8 && <span style={{ fontFamily: T.font.b, fontSize: '12px', color: T.textSec }}>{tr(language, 'or_more')}</span>}
            </button>
          ))}
        </div>
      </div>
    </StepWrap>
    );
  };

  /* ═══════ STEP: DRINK TYPE (simplified, centered) ═══════ */
  const DrinkTypeStep = () => {
    const cafeOpen = isCafeOpen(flowConfig?.cafe_cutoff_hour);
    const greeting = partySize === 1 ? tr(language, 'drink_type_greet_solo') : tr(language, 'drink_type_greet_group', { n: partySize });
    const copy = getCopy('drink_type', `${greeting}\n${tr(language, 'drink_type_question')}`, '');

    return (
      <StepWrap stepKey="drink_type">
        <TopBar />
        <div>
          <CenteredBlock>
            <p style={{ ...S.question, textAlign: 'center', whiteSpace: 'pre-line' }}>
              {copy.title}
            </p>
            {copy.subtitle && <p style={{ ...S.sub, textAlign: 'center' }}>{copy.subtitle}</p>}
          </CenteredBlock>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '520px', margin: '24px auto 0', width: '100%' }}>
            <button data-testid="drink-type-alcohol" onClick={() => { setDrinkType('alcohol'); setStep('drink_cat'); }}
              style={{ ...S.card, justifyContent: 'center', padding: '28px' }}>
              <Wine size={28} color={T.gold} strokeWidth={1.5} />
              <span style={{ fontFamily: T.font.b, fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 600, color: T.text }}>
                {tr(language, 'with_alcohol')}
              </span>
            </button>
            <button data-testid="drink-type-no-alcohol" onClick={() => { setDrinkType('no_alcohol'); setStep('drink_cat'); }}
              style={{ ...S.card, justifyContent: 'center', padding: '28px' }}>
              <GlassWater size={28} color={T.gold} strokeWidth={1.5} />
              <span style={{ fontFamily: T.font.b, fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 600, color: T.text }}>
                {tr(language, 'no_alcohol')}
              </span>
            </button>
          </div>

          {/* Cross link: solo si aún no han visto la comida */}
          {!foodCat && selectedFood.length === 0 && (
            <div style={{ ...S.actionsCol, marginTop: '20px' }}>
              <button data-testid="drink-type-prefer-food" onClick={() => setStep('food_cat')} style={{
                ...S.ghostBtn, border: 'none', color: T.textSec, fontSize: 'clamp(18px, 1.8vw, 20px)',
              }}>
                {tr(language, 'prefer_food_first')}
              </button>
            </div>
          )}
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: DRINK CATEGORY ═══════ */
  const DrinkCatStep = () => {
    const cafeOpen = isCafeOpen(flowConfig?.cafe_cutoff_hour);
    const copy = getCopy('drink_cat',
      drinkType === 'alcohol' ? tr(language, 'drink_cat_alcohol_title') : tr(language, 'drink_cat_no_alcohol_title'),
      tr(language, 'drink_cat_subtitle')
    );
    let cats;
    if (drinkType === 'alcohol') {
      cats = ALCOHOL_CATS;
    } else {
      // Mostrar SIEMPRE todas las categorías sin alcohol (sin filtro horario).
      // El camarero digital debe ofrecer la carta completa a cualquier hora.
      cats = NO_ALCOHOL_CATS;
    }
    const available = cats.filter(c => productsInCat(c.id).length > 0);

    return (
      <StepWrap stepKey="drink_cat" wide>
        <TopBar />
        <div>
          <CenteredBlock>
            <p style={{ ...S.question, textAlign: 'center', marginBottom: '32px' }}>{copy.title}</p>
          </CenteredBlock>

          <div style={(() => {
            if (!isTablet) {
              // Mobile → full-width vertical banner stack
              return {
                display: 'flex', flexDirection: 'column', gap: '14px',
                width: '100%', maxWidth: '480px', margin: '0 auto',
              };
            }
            // Tablet → 2-column grid (bigger cards). For odd counts the last card
            // simply takes a single column; the grid auto-fills.
            return {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '22px',
              width: '100%',
              maxWidth: '720px',
              margin: '0 auto',
            };
          })()}>
            {available.map(cat => {
              const bgImg = catImage(cat.id);
              return (
                <button key={cat.id} data-testid={`drink-cat-${cat.id}`}
                  onClick={() => {
                    setDrinkCat(cat.id);
                    setCarouselIndex(0);
                    // SIEMPRE ir a recomendación — el paso auto-detecta premium
                    setStep('drink_rec');
                  }}
                  style={{
                    position: 'relative', aspectRatio: isTablet ? '16/10' : '16/9',
                    borderRadius: '24px', overflow: 'hidden',
                    border: `1px solid ${T.border}`, cursor: 'pointer', padding: 0, textAlign: 'left',
                  }}
                >
                  <img src={optimizedImageUrl(bgImg, 800)} alt="" {...lazyImgProps} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.1) 55%)',
                  }} />
                  <div style={{ position: 'absolute', bottom: '20px', left: '22px', right: '22px', textAlign: 'center' }}>
                    <p style={{ fontFamily: T.font.b, fontSize: isTablet ? 'clamp(28px, 3.3vw, 36px)' : '24px', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.01em', textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                      {catLabel(cat.id, language)}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: DRINK RECOMMENDATION (auto-premium) ═══════ */
  const DrinkRecStep = () => {
    const catName = catLabel(drinkCat, language);
    const catProducts = productsInCat(drinkCat);

    // Get recommendation: manual config OR auto-pick by badges (premium > bestseller > expensive)
    const manualRec = getRecForCat(drinkCat);
    let recProducts;
    let recTitle;
    let recSubtitle;

    if (manualRec) {
      recProducts = manualRec.products;
      recTitle = manualRec.title;
      recSubtitle = manualRec.subtitle;
    } else {
      // Auto-recommendation driven by badges
      recProducts = getAutoRecommendations(catProducts, 2);
      recTitle = null;
      recSubtitle = null;
    }

    // If no products at all, skip (defer to effect to avoid setState-during-render in StrictMode)
    useEffect(() => {
      if (!recProducts || recProducts.length === 0) setStep('drink_select');
    }, [recProducts]);
    if (!recProducts || recProducts.length === 0) return null;

    // Conversational copy based on what badges the product has
    const topProduct = recProducts[0];
    const isPremium = topProduct.badge_premium || topProduct.is_premium;
    const isBestseller = topProduct.badge_bestseller || topProduct.is_featured;

    let autoTitle;
    if (isPremium && recProducts.length > 1) {
      autoTitle = tr(language, 'rec_premium_multi', { cat: catName.toLowerCase() });
    } else if (isPremium) {
      autoTitle = tr(language, 'rec_premium_single', { name: loc(topProduct.name) });
    } else if (isBestseller) {
      autoTitle = tr(language, 'rec_bestseller_drink', { cat: catName.toLowerCase() });
    } else {
      autoTitle = tr(language, 'rec_default_drink', { cat: catName.toLowerCase() });
    }

    const autoSub = isPremium
      ? (loc(topProduct.description) ? loc(topProduct.description).slice(0, 120) : tr(language, 'rec_premium_sub'))
      : isBestseller
        ? tr(language, 'rec_bestseller_sub')
        : (loc(topProduct.description) ? loc(topProduct.description).slice(0, 100) : '');

    const hasOpts = (p) => p.options?.length > 0 || p.supplements?.length > 0 || p.requires_selection;
    const handleQuickAdd = (e, p) => {
      e.stopPropagation();
      if (hasOpts(p)) { openProductDetail(p, 'drink'); return; }
      setTempSelection({ ...p, qty: 1 });
      setStep('drink_confirm');
    };
    const handleProductClick = (p) => openProductDetail(p, 'drink');
    const dummyT = (key) => {
      const map = { addToSelection: tr(language, 'add_btn'), viewDetails: 'Ver', noResults: 'Sin resultados' };
      return map[key] || key;
    };
    const portrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;

    return (
      <StepWrap stepKey="drink_rec">
        <TopBar />
        <div style={{
          flex: portrait ? '0 0 auto' : 1,
          display: 'flex', flexDirection: 'column',
          overflow: portrait ? 'visible' : 'hidden', minHeight: 0,
        }}>
          <CenteredBlock style={{ maxWidth: portrait ? '480px' : '100%', marginTop: '4px', marginBottom: portrait ? '18px' : '8px' }}>
            <p style={{ ...S.question, textAlign: 'center', marginBottom: 0 }}>
              {recTitle || autoTitle}
            </p>
          </CenteredBlock>

          <div style={{
            margin: '0 -24px',
            flex: portrait ? '0 0 auto' : 1,
            overflow: portrait ? 'visible' : 'hidden', minHeight: 0,
          }}>
            <ProductCarousel
              products={recProducts.filter(p => p && p.id)}
              activeIndex={carouselIndex} onIndexChange={setCarouselIndex}
              onQuickAdd={handleQuickAdd} onProductClick={handleProductClick}
              lastAdded={null} getImage={getProductImage}
              localize={loc} language={language} t={dummyT} isDark={!sunMode}
            />
          </div>

          <div style={{ ...S.actionsCol, padding: portrait ? '6px 0 0' : '12px 0 0' }}>
            <button data-testid="skip-drink-rec" onClick={() => setStep('drink_select')} style={{
              ...S.ghostBtn, border: 'none', color: T.gold,
              fontSize: '19px', fontWeight: 600, padding: '12px', width: '100%',
              textDecoration: 'underline', textUnderlineOffset: '4px',
            }}>
              {tr(language, 'see_all_drinks', { cat: catName.toLowerCase() })}
            </button>
          </div>
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: DRINK SELECT (ProductCarousel) ═══════ */
  const DrinkSelectStep = () => {
    const products = productsInCat(drinkCat);
    const catName = catLabel(drinkCat, language);

    const handleQuickAdd = useCallback((e, product) => {
      e.stopPropagation();
      // If product has options, open detail view
      const hasOpts = product.options?.length > 0 || product.supplements?.length > 0 || product.requires_selection;
      if (hasOpts) {
        openProductDetail(product, 'drink');
        return;
      }
      setTempSelection({ ...product, qty: 1 });
      setStep('drink_confirm');
    }, [openProductDetail]);

    const handleProductClick = useCallback((product) => {
      openProductDetail(product, 'drink');
    }, [openProductDetail]);

    const dummyT = useCallback((key) => {
      const map = { addToSelection: tr(language, 'add_btn'), viewDetails: 'Ver', noResults: 'Sin resultados' };
      return map[key] || key;
    }, []);
    const portrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;

    return (
      <StepWrap stepKey="drink_select">
        <TopBar />
        <div style={{
          flex: portrait ? '0 0 auto' : 1,
          display: 'flex', flexDirection: 'column',
          overflow: portrait ? 'visible' : 'hidden', minHeight: 0,
        }}>
          <CenteredBlock style={{ maxWidth: portrait ? '480px' : '100%' }}>
            <p style={{ ...S.question, marginBottom: portrait ? '18px' : '12px', textAlign: 'center' }}>{catName}</p>
          </CenteredBlock>

          {products.length > 0 && (
            <div style={{
              margin: '0 -24px',
              flex: portrait ? '0 0 auto' : 1,
              overflow: portrait ? 'visible' : 'hidden', minHeight: 0,
            }}>
              <ProductCarousel
                products={products.filter(p => p && p.id)}
                activeIndex={carouselIndex} onIndexChange={setCarouselIndex}
                onQuickAdd={handleQuickAdd} onProductClick={handleProductClick}
                lastAdded={null} getImage={getProductImage}
                localize={loc} language={language} t={dummyT} isDark={!sunMode}
              />
            </div>
          )}
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: DRINK CONFIRM ═══════ */
  const DrinkConfirmStep = () => {
    const allDrinks = [...selectedDrinks, ...(tempSelection ? [{ ...tempSelection, qty: 1 }] : [])];

    return (
      <StepWrap stepKey="drink_confirm">
        <TopBar />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div>
            <p style={{ fontFamily: T.font.b, fontSize: '13px', fontWeight: 500, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: T.gold, marginBottom: '16px', textAlign: 'center' }}>
              {tr(language, 'your_drinks')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '460px', margin: '0 auto' }}>
              {allDrinks.map((d, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
                  background: T.bgEl, border: `1px solid ${T.border}`, borderRadius: '14px',
                }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: `${T.gold}22`, border: `1px solid ${T.gold}44`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Check size={14} color={T.gold} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontFamily: T.font.b, fontSize: '15px', color: T.text, display: 'block' }}>
                      {loc(d.name)}
                    </span>
                    {d.selectedOptions?.length > 0 && (
                      <span style={{ fontFamily: T.font.b, fontSize: '12px', color: T.textSec }}>
                        {d.selectedOptions.map(o => loc(o.name)).join(', ')}
                      </span>
                    )}
                  </div>
                  <span style={S.price}>{d.price?.toFixed(2)}€</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', ...S.actionsCol }}>
            <button data-testid="drink-confirm-ok" onClick={() => {
              setSelectedDrinks(allDrinks);
              setTempSelection(null);
              setStep('food_cat');
            }} style={S.goldBtn}>
              {tr(language, 'drinks_done')}
            </button>
            <button data-testid="drink-confirm-another" onClick={() => {
              setSelectedDrinks(allDrinks);
              setTempSelection(null);
              setStep('drink_type');
            }} style={S.ghostBtn}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Plus size={18} /> {tr(language, 'add_another_drink')}
              </span>
            </button>
            <button data-testid="drink-confirm-change" onClick={() => setStep('drink_select')} style={{
              ...S.ghostBtn, border: 'none', color: T.textSec, padding: '10px',
            }}>
              {tr(language, 'change_last')}
            </button>
          </div>
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: FOOD PROMPT ═══════ */
  const FoodPromptStep = () => {
    const copy = getCopy('food_prompt', tr(language, 'food_prompt_title'), tr(language, 'food_prompt_sub'));
    return (
      <StepWrap stepKey="food_prompt">
        <TopBar />
        <div>
          <CenteredBlock style={{ marginBottom: '24px' }}>
            <p style={{ ...S.question, textAlign: 'center' }}>{copy.title}</p>
            <p style={{ ...S.sub, textAlign: 'center' }}>{copy.subtitle}</p>
          </CenteredBlock>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', ...S.actionsCol }}>
            <button data-testid="food-prompt-yes" onClick={() => setStep('food_cat')} style={S.goldBtn}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <UtensilsCrossed size={18} /> {tr(language, 'food_yes')}
              </span>
            </button>
            <button data-testid="food-prompt-no" onClick={() => setStep('summary')} style={S.ghostBtn}>
              {tr(language, 'food_no')}
            </button>
          </div>
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: FOOD CATEGORY ═══════ */
  const FoodCatStep = () => {
    const available = FOOD_CATS.filter(c => productsInCat(c.id).length > 0);
    const copy = getCopy('food_cat', tr(language, 'food_cat_title'), tr(language, 'food_cat_sub'));

    return (
      <StepWrap stepKey="food_cat" wide>
        <TopBar />
        <div>
          <CenteredBlock>
            <p style={{ ...S.question, textAlign: 'center', marginBottom: '32px' }}>{copy.title}</p>
          </CenteredBlock>

          <div style={(() => {
            if (!isTablet) {
              return {
                display: 'flex', flexDirection: 'column', gap: '14px',
                width: '100%', maxWidth: '480px', margin: '0 auto',
              };
            }
            // Tablet → 2-column grid (bigger cards). Odd counts auto-fill.
            return {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '22px',
              width: '100%',
              maxWidth: '720px',
              margin: '0 auto',
            };
          })()}>
            {available.map(cat => {
              const bgImg = catImage(cat.id);
              return (
                <button key={cat.id} data-testid={`food-cat-${cat.id}`}
                  onClick={() => {
                    setFoodCat(cat.id);
                    setCarouselIndex(0);
                    // SIEMPRE ir a recomendación — auto-detecta premium
                    setStep('food_rec');
                  }}
                  style={{
                    position: 'relative', aspectRatio: isTablet ? '16/10' : '16/9',
                    borderRadius: '24px', overflow: 'hidden',
                    border: `1px solid ${T.border}`, cursor: 'pointer', padding: 0, textAlign: 'left',
                  }}
                >
                  <img src={optimizedImageUrl(bgImg, 800)} alt="" {...lazyImgProps} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.1) 55%)',
                  }} />
                  <div style={{ position: 'absolute', bottom: '20px', left: '22px', right: '22px', textAlign: 'center' }}>
                    <p style={{ fontFamily: T.font.b, fontSize: isTablet ? 'clamp(28px, 3.3vw, 36px)' : '24px', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.01em', textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                      {catLabel(cat.id, language)}
                    </p>
                  </div>
                </button>
              );
            })}
            {/* Decorative logo tile to fill the empty grid slot when count is odd (tablet only) */}
            {isTablet && available.length % 2 === 1 && (
              <div aria-hidden="true" style={{
                position: 'relative', aspectRatio: '16/10', borderRadius: '24px', overflow: 'hidden',
                border: `1px solid ${T.border}`, background: '#000',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img src="/pont10-logo.png" alt="" loading="lazy"
                  style={{ width: '70%', height: '70%', objectFit: 'contain', opacity: 0.85 }} />
              </div>
            )}
          </div>

          {/* Cross link: si ya hay bebida pedida, ofrecemos saltar comida.
              Si todavía no hay bebida (llegó aquí desde "prefiero ver primero la comida"),
              no mostramos nada — el usuario puede volver con "Atrás". */}
          {selectedDrinks.length > 0 && (
            <div style={{ ...S.actionsCol, marginTop: '28px' }}>
              <button data-testid="food-cat-skip" onClick={() => setStep('summary')} style={{
                ...S.ghostBtn, border: 'none', color: T.textSec, fontSize: 'clamp(18px, 1.8vw, 20px)',
              }}>
                {tr(language, 'food_no')}
              </button>
            </div>
          )}
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: FOOD RECOMMENDATION (auto-premium) ═══════ */
  const FoodRecStep = () => {
    const catName = catLabel(foodCat, language);
    const catProducts = productsInCat(foodCat);

    // Get recommendation: manual config OR auto-pick by badges (premium > bestseller > expensive)
    const manualRec = getRecForCat(foodCat);
    let recProducts;
    let recTitle;
    let recSubtitle;

    if (manualRec) {
      recProducts = manualRec.products;
      recTitle = manualRec.title;
      recSubtitle = manualRec.subtitle;
    } else {
      recProducts = getAutoRecommendations(catProducts, 2);
      recTitle = null;
      recSubtitle = null;
    }

    useEffect(() => {
      if (!recProducts || recProducts.length === 0) setStep('food_select');
    }, [recProducts]);
    if (!recProducts || recProducts.length === 0) return null;

    const topProduct = recProducts[0];
    const isPremium = topProduct.badge_premium || topProduct.is_premium;
    const isBestseller = topProduct.badge_bestseller || topProduct.is_featured;

    let autoTitle;
    if (isPremium) {
      autoTitle = tr(language, 'rec_premium_food', { cat: catName.toLowerCase() });
    } else if (isBestseller) {
      autoTitle = tr(language, 'rec_bestseller_food', { cat: catName.toLowerCase() });
    } else {
      autoTitle = tr(language, 'rec_default_food', { cat: catName.toLowerCase() });
    }

    const autoSub = isPremium
      ? (loc(topProduct.description) ? loc(topProduct.description).slice(0, 120) : tr(language, 'rec_premium_sub'))
      : isBestseller
        ? tr(language, 'rec_bestseller_sub')
        : (loc(topProduct.description) ? loc(topProduct.description).slice(0, 100) : '');

    const hasOpts = (p) => p.options?.length > 0 || p.supplements?.length > 0 || p.requires_selection;
    const handleQuickAdd = (e, p) => {
      e.stopPropagation();
      if (hasOpts(p)) { openProductDetail(p, 'food'); return; }
      setTempSelection({ ...p, qty: 1 });
      setStep('food_confirm');
    };
    const handleProductClick = (p) => openProductDetail(p, 'food');
    const dummyT = (key) => {
      const map = { addToSelection: tr(language, 'add_btn'), viewDetails: 'Ver', noResults: 'Sin resultados' };
      return map[key] || key;
    };
    const portrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;

    return (
      <StepWrap stepKey="food_rec">
        <TopBar />
        <div style={{
          flex: portrait ? '0 0 auto' : 1,
          display: 'flex', flexDirection: 'column',
          overflow: portrait ? 'visible' : 'hidden', minHeight: 0,
        }}>
          <CenteredBlock style={{ maxWidth: portrait ? '480px' : '100%', marginTop: '4px', marginBottom: portrait ? '18px' : '8px' }}>
            <p style={{ ...S.question, textAlign: 'center', marginBottom: 0 }}>
              {recTitle || autoTitle}
            </p>
          </CenteredBlock>

          <div style={{
            margin: '0 -24px',
            flex: portrait ? '0 0 auto' : 1,
            overflow: portrait ? 'visible' : 'hidden', minHeight: 0,
          }}>
            <ProductCarousel
              products={recProducts.filter(p => p && p.id)}
              activeIndex={carouselIndex} onIndexChange={setCarouselIndex}
              onQuickAdd={handleQuickAdd} onProductClick={handleProductClick}
              lastAdded={null} getImage={getProductImage}
              localize={loc} language={language} t={dummyT} isDark={!sunMode}
            />
          </div>

          <div style={{ ...S.actionsCol, padding: portrait ? '6px 0 0' : '12px 0 0' }}>
            <button data-testid="skip-food-rec" onClick={() => setStep('food_select')} style={{
              ...S.ghostBtn, border: 'none', color: T.gold,
              fontSize: '19px', fontWeight: 600, padding: '12px', width: '100%',
              textDecoration: 'underline', textUnderlineOffset: '4px',
            }}>
              {tr(language, 'see_all_food', { cat: catName.toLowerCase() })}
            </button>
          </div>
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: FOOD SELECT (ProductCarousel) ═══════ */
  const FoodSelectStep = () => {
    const products = productsInCat(foodCat);
    const catName = catLabel(foodCat, language);

    const handleQuickAdd = useCallback((e, product) => {
      e.stopPropagation();
      const hasOpts = product.options?.length > 0 || product.supplements?.length > 0 || product.requires_selection;
      if (hasOpts) {
        openProductDetail(product, 'food');
        return;
      }
      setTempSelection({ ...product, qty: 1 });
      setStep('food_confirm');
    }, [openProductDetail]);

    const handleProductClick = useCallback((product) => {
      openProductDetail(product, 'food');
    }, [openProductDetail]);

    const dummyT = useCallback((key) => {
      const map = { addToSelection: tr(language, 'add_btn'), viewDetails: 'Ver', noResults: 'Sin resultados' };
      return map[key] || key;
    }, []);
    const portrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;

    return (
      <StepWrap stepKey="food_select">
        <TopBar />
        <div style={{
          flex: portrait ? '0 0 auto' : 1,
          display: 'flex', flexDirection: 'column',
          overflow: portrait ? 'visible' : 'hidden', minHeight: 0,
        }}>
          <CenteredBlock style={{ maxWidth: portrait ? '480px' : '100%' }}>
            <p style={{ ...S.question, marginBottom: portrait ? '18px' : '12px', textAlign: 'center' }}>{catName}</p>
          </CenteredBlock>

          {products.length > 0 && (
            <div style={{
              margin: '0 -24px',
              flex: portrait ? '0 0 auto' : 1,
              overflow: portrait ? 'visible' : 'hidden', minHeight: 0,
            }}>
              <ProductCarousel
                products={products.filter(p => p && p.id)}
                activeIndex={carouselIndex} onIndexChange={setCarouselIndex}
                onQuickAdd={handleQuickAdd} onProductClick={handleProductClick}
                lastAdded={null} getImage={getProductImage}
                localize={loc} language={language} t={dummyT} isDark={!sunMode}
              />
            </div>
          )}
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: FOOD CONFIRM ═══════ */
  const FoodConfirmStep = () => {
    const allFood = [...selectedFood, ...(tempSelection ? [{ ...tempSelection, qty: 1 }] : [])];

    return (
      <StepWrap stepKey="food_confirm">
        <TopBar />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div>
            <p style={{ fontFamily: T.font.b, fontSize: '13px', fontWeight: 500, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: T.gold, marginBottom: '16px', textAlign: 'center' }}>
              {tr(language, 'your_food')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '460px', margin: '0 auto' }}>
              {allFood.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
                  background: T.bgEl, border: `1px solid ${T.border}`, borderRadius: '14px',
                }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: `${T.gold}22`, border: `1px solid ${T.gold}44`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Check size={14} color={T.gold} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontFamily: T.font.b, fontSize: '15px', color: T.text, display: 'block' }}>
                      {loc(f.name)}
                    </span>
                    {f.selectedOptions?.length > 0 && (
                      <span style={{ fontFamily: T.font.b, fontSize: '12px', color: T.textSec }}>
                        {f.selectedOptions.map(o => loc(o.name)).join(', ')}
                      </span>
                    )}
                  </div>
                  <span style={S.price}>{f.price?.toFixed(2)}€</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', ...S.actionsCol }}>
            <button data-testid="food-confirm-ok" onClick={() => {
              setSelectedFood(allFood);
              setTempSelection(null);
              goToWithUpsell('summary', 'food_confirm');
            }} style={S.goldBtn}>
              {tr(language, 'food_done')}
            </button>
            <button data-testid="food-confirm-more" onClick={() => {
              setSelectedFood(allFood);
              setTempSelection(null);
              setStep('food_cat');
            }} style={S.ghostBtn}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Plus size={18} /> {tr(language, 'add_more_food')}
              </span>
            </button>
            <button data-testid="food-confirm-change" onClick={() => setStep('food_select')} style={{
              ...S.ghostBtn, border: 'none', color: T.textSec, padding: '10px',
            }}>
              {tr(language, 'change_last_food')}
            </button>
          </div>
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: SUMMARY ═══════ */
  const SummaryStep = () => {
    const totalDrinks = selectedDrinks.reduce((s, d) => s + (d.price * (d.qty || 1)), 0);
    const totalFood = selectedFood.reduce((s, f) => s + (f.price * (f.qty || 1)), 0);
    const total = totalDrinks + totalFood;

    return (
      <StepWrap stepKey="summary">
        <TopBar />
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <p style={{ ...S.question, textAlign: 'center' }}>{tr(language, 'your_order')}</p>
          <p style={{ ...S.sub, textAlign: 'center' }}>{partySize} {partySize === 1 ? tr(language, 'person_one') : tr(language, 'person_many')}{tableNumber ? ` · ${tr(language, 'table')} ${tableNumber}` : ''}</p>

          {selectedDrinks.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontFamily: T.font.b, fontSize: '12px', fontWeight: 500, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: T.gold, marginBottom: '12px' }}>
                {tr(language, 'drinks_label')}
              </p>
              {selectedDrinks.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontFamily: T.font.b, fontSize: '13px', color: T.textSec, width: '24px' }}>
                      x{d.qty || 1}
                    </span>
                    <div>
                      <span style={{ fontFamily: T.font.b, fontSize: '15px', color: T.text, display: 'block' }}>{loc(d.name)}</span>
                      {d.selectedOptions?.length > 0 && (
                        <span style={{ fontFamily: T.font.b, fontSize: '12px', color: T.textSec }}>
                          {d.selectedOptions.map(o => loc(o.name)).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={S.price}>{(d.price * (d.qty || 1)).toFixed(2)}€</span>
                </div>
              ))}
            </div>
          )}

          {selectedFood.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontFamily: T.font.b, fontSize: '12px', fontWeight: 500, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: T.gold, marginBottom: '12px' }}>
                {tr(language, 'food_label')}
              </p>
              {selectedFood.map((f, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontFamily: T.font.b, fontSize: '13px', color: T.textSec, width: '24px' }}>
                      x{f.qty || 1}
                    </span>
                    <div>
                      <span style={{ fontFamily: T.font.b, fontSize: '15px', color: T.text, display: 'block' }}>{loc(f.name)}</span>
                      {f.selectedOptions?.length > 0 && (
                        <span style={{ fontFamily: T.font.b, fontSize: '12px', color: T.textSec }}>
                          {f.selectedOptions.map(o => loc(o.name)).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={S.price}>{(f.price * (f.qty || 1)).toFixed(2)}€</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px 0', marginTop: '8px' }}>
            <span style={{ fontFamily: T.font.h, fontSize: '20px', color: T.text }}>{tr(language, 'total')}</span>
            <span style={{ fontFamily: T.font.h, fontSize: '24px', fontWeight: 600, color: T.gold }}>
              {total.toFixed(2)}€
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px', ...S.actionsCol }}>
            <button data-testid="flow-confirm-order" style={S.goldBtn} onClick={() => setShowWaiterModal(true)}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Check size={18} /> {tr(language, 'confirm_order')}
              </span>
            </button>
            <button data-testid="flow-add-more" onClick={() => setStep('add_more_choice')} style={S.ghostBtn}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Plus size={18} /> {tr(language, 'add_more')}
              </span>
            </button>
          </div>
        </div>
      </StepWrap>
    );
  };

  /* ═══════ STEP: ADD MORE CHOICE (drink vs food) ═══════ */
  const AddMoreChoiceStep = () => (
    <StepWrap stepKey="add_more_choice">
      <TopBar />
      <div>
        <CenteredBlock>
          <p style={{ ...S.question, textAlign: 'center', marginBottom: '32px' }}>
            {tr(language, 'add_more_title')}
          </p>
        </CenteredBlock>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '520px', margin: '0 auto', width: '100%' }}>
          <button data-testid="add-more-drink" onClick={() => setStep('drink_type')}
            style={{ ...S.card, justifyContent: 'center', padding: '28px' }}>
            <Wine size={28} color={T.gold} strokeWidth={1.5} />
            <span style={{ fontFamily: T.font.b, fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 600, color: T.text }}>
              {tr(language, 'add_more_drink')}
            </span>
          </button>
          <button data-testid="add-more-food" onClick={() => setStep('food_cat')}
            style={{ ...S.card, justifyContent: 'center', padding: '28px' }}>
            <UtensilsCrossed size={28} color={T.gold} strokeWidth={1.5} />
            <span style={{ fontFamily: T.font.b, fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 600, color: T.text }}>
              {tr(language, 'add_more_food')}
            </span>
          </button>
        </div>
      </div>
    </StepWrap>
  );

  /* ═══════ STEP: UPSELL (dynamic from config) ═══════ */
  const UpsellStep = () => {
    if (!upsellData) return null;
    const suggestedCats = (upsellData.suggested_categories || [])
      .map(catId => [...ALCOHOL_CATS, ...NO_ALCOHOL_CATS, ...FOOD_CATS].find(c => c.id === catId))
      .filter(Boolean)
      .filter(c => productsInCat(c.id).length > 0);

    const suggestedProds = (upsellData.suggested_products || [])
      .map(pid => allProducts.find(p => p.id === pid))
      .filter(Boolean);

    // Determine destination step based on context
    const skipDest = prevStepBeforeUpsell === 'drink_confirm' ? 'food_prompt' : 'summary';
    const handleSkip = () => { setUpsellData(null); setStep(skipDest === 'food_prompt' ? 'food_prompt' : 'summary'); };

    const handleCatSelect = (catId) => {
      setUpsellData(null);
      setFoodCat(catId);
      setCarouselIndex(0);
      // SIEMPRE ir a recomendación — auto-detecta premium
      setStep('food_rec');
    };

    const handleProductSelect = (p) => {
      setUpsellData(null);
      const hasOpts = p.options?.length > 0 || p.supplements?.length > 0;
      if (hasOpts) {
        openProductDetail(p, 'food');
      } else {
        setTempSelection({ ...p, qty: 1 });
        setStep('food_confirm');
      }
    };

    return (
      <StepWrap stepKey="upsell">
        <TopBar />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <CenteredBlock>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%', margin: '0 auto 16px',
              background: `${T.gold}15`, border: `1px solid ${T.gold}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles size={22} color={T.gold} />
            </div>
            <p style={{ ...S.question, textAlign: 'center', marginBottom: 0 }}>{upsellData.title}</p>
          </CenteredBlock>

          {/* Suggested specific products (if configured) */}
          {suggestedProds.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '460px', margin: '0 auto', width: '100%' }}>
              {suggestedProds.map(p => (
                <button key={p.id} data-testid={`upsell-product-${p.id}`}
                  onClick={() => handleProductSelect(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px',
                    background: T.bgEl, border: `1px solid ${T.border}`, borderRadius: '16px',
                    cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all 0.2s',
                  }}
                >
                  {(p.image_url || p.images?.[0]) && (
                    <img src={optimizedImageUrl(p.image_url || p.images[0], 200)} alt="" {...lazyImgProps} style={{ width: '56px', height: '56px', borderRadius: '12px', objectFit: 'cover' }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: T.font.b, fontSize: '15px', fontWeight: 600, color: T.text, margin: 0 }}>{loc(p.name)}</p>
                    {socialProof && (p.badge_bestseller || p.is_featured) && (
                      <p style={{ fontFamily: T.font.b, fontSize: '11px', color: T.gold, margin: '2px 0 0', fontWeight: 600 }}>
                        {socialProof.bestseller_label || tr(language, 'badge_bestseller_default')}
                      </p>
                    )}
                  </div>
                  <span style={{ fontFamily: T.font.h, fontSize: '18px', fontWeight: 600, color: T.gold }}>{p.price?.toFixed(2)}€</span>
                </button>
              ))}
            </div>
          )}

          {/* Suggested categories */}
          {suggestedCats.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '14px', width: '100%', maxWidth: suggestedCats.length <= 3 ? `${suggestedCats.length * 200}px` : '100%', margin: suggestedCats.length <= 3 ? '0 auto' : '0' }}>
              {suggestedCats.map(cat => {
                const bgImg = catImage(cat.id);
                return (
                  <button key={cat.id} data-testid={`upsell-cat-${cat.id}`}
                    onClick={() => handleCatSelect(cat.id)}
                    style={{
                      position: 'relative', aspectRatio: '1.2', borderRadius: '20px', overflow: 'hidden',
                      border: `1px solid ${T.border}`, cursor: 'pointer', padding: 0, textAlign: 'left',
                    }}
                  >
                    <img src={optimizedImageUrl(bgImg, 400)} alt="" {...lazyImgProps} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.1) 60%)' }} />
                    <div style={{ position: 'absolute', bottom: '12px', left: '12px', right: '12px' }}>
                      <p style={{ fontFamily: T.font.b, fontSize: '14px', fontWeight: 700, color: T.text, margin: 0 }}>{catLabel(cat.id, language)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Skip */}
          <div style={{ ...S.actionsCol }}>
            <button data-testid="upsell-skip" onClick={handleSkip} style={{
              ...S.ghostBtn, border: 'none', color: T.textSec, fontSize: 'clamp(15px, 1.6vw, 17px)',
            }}>
              {upsellData.skip_label || tr(language, 'food_no')}
            </button>
          </div>
        </div>
      </StepWrap>
    );
  };

  /* ═══════ RENDER ═══════ */
  if (!dataLoaded) {
    return (
      <div style={{ width: '100%', height: '100dvh', background: T.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '24px', height: '24px', border: `2px solid ${T.border}`, borderTopColor: T.gold,
          borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const stepMap = {
    welcome: WelcomeStep, party_size: PartySizeStep,
    drink_type: DrinkTypeStep, drink_cat: DrinkCatStep, drink_rec: DrinkRecStep,
    drink_select: DrinkSelectStep, drink_confirm: DrinkConfirmStep,
    upsell: UpsellStep,
    food_prompt: FoodPromptStep, food_cat: FoodCatStep, food_rec: FoodRecStep,
    food_select: FoodSelectStep, food_confirm: FoodConfirmStep, summary: SummaryStep,
    add_more_choice: AddMoreChoiceStep,
  };
  const CurrentStep = stepMap[step] || WelcomeStep;

  return (
    <div data-testid="guided-flow" style={{
      width: '100%', height: '100dvh',
      background: T.bg, position: 'relative', overflow: 'hidden',
    }}>
      <CurrentStep />
      {/* Product Detail Overlay */}
      {detailProduct && (
        <ProductDetailOverlay
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onAdd={handleDetailAdd}
          localize={loc}
        />
      )}
      {/* Waiter Modal — shown after "Finalizar y mostrar al camarero" */}
      {showWaiterModal && (
        <div data-testid="waiter-modal" onClick={() => setShowWaiterModal(false)} style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: T.bgEl, border: `1px solid ${T.gold}55`,
            borderRadius: '24px', width: '100%', maxWidth: '520px',
            maxHeight: 'calc(100dvh - 60px)', overflow: 'auto',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: '28px 28px 18px', textAlign: 'center', borderBottom: `1px solid ${T.border}` }}>
              <p style={{ fontFamily: T.font.b, fontSize: '17px', fontWeight: 600, letterSpacing: '0.2em',
                textTransform: 'uppercase', color: T.gold, marginBottom: '12px' }}>
                {tableNumber ? `${tr(language, 'table')} ${tableNumber}` : ''}
                {tableNumber ? ' · ' : ''}
                {partySize} {partySize === 1 ? tr(language, 'person_one') : tr(language, 'person_many')}
              </p>
              <p style={{ fontFamily: T.font.h, fontSize: 'clamp(24px, 5.5vw, 45px)', fontWeight: 600, color: T.text, margin: 0, lineHeight: 1.15 }}>
                {tr(language, 'waiter_modal_title')}
              </p>
            </div>

            <div style={{ padding: '22px 28px 8px' }}>
              {selectedDrinks.length > 0 && (
                <div style={{
                  marginBottom: '20px',
                  background: 'rgba(76, 134, 200, 0.08)',
                  border: '1px solid rgba(76, 134, 200, 0.25)',
                  borderLeft: '5px solid #4C86C8',
                  borderRadius: '14px', padding: '14px 18px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <GlassWater size={26} color="#7AB3E8" strokeWidth={2} />
                    <p style={{ fontFamily: T.font.b, fontSize: '30px', fontWeight: 700, letterSpacing: '0.18em',
                      textTransform: 'uppercase', color: '#7AB3E8', margin: 0 }}>
                      {tr(language, 'drinks_label')}
                    </p>
                  </div>
                  {selectedDrinks.map((d, i) => (
                    <WaiterLine key={`d${i}`} item={d} loc={loc} locES={locES} language={language} T={T} isLast={i === selectedDrinks.length - 1} />
                  ))}
                </div>
              )}
              {selectedFood.length > 0 && (
                <div style={{
                  marginBottom: '20px',
                  background: 'rgba(220, 138, 90, 0.08)',
                  border: '1px solid rgba(220, 138, 90, 0.25)',
                  borderLeft: '5px solid #DC8A5A',
                  borderRadius: '14px', padding: '14px 18px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <UtensilsCrossed size={26} color="#F0A878" strokeWidth={2} />
                    <p style={{ fontFamily: T.font.b, fontSize: 'clamp(16px, 3.5vw, 30px)', fontWeight: 700, letterSpacing: '0.18em',
                      textTransform: 'uppercase', color: '#F0A878', margin: 0 }}>
                      {tr(language, 'food_label')}
                    </p>
                  </div>
                  {selectedFood.map((f, i) => (
                    <WaiterLine key={`f${i}`} item={f} loc={loc} locES={locES} language={language} T={T} isLast={i === selectedFood.length - 1} />
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '22px 0 6px', borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontFamily: T.font.h, fontSize: '36px', color: T.text }}>{tr(language, 'total')}</span>
                <span style={{ fontFamily: T.font.h, fontSize: 'clamp(26px, 5.5vw, 48px)', fontWeight: 600, color: T.gold }}>
                  {(selectedDrinks.reduce((s, d) => s + (d.price * (d.qty || 1)), 0)
                    + selectedFood.reduce((s, f) => s + (f.price * (f.qty || 1)), 0)).toFixed(2)}€
                </span>
              </div>
            </div>

            <div style={{ padding: '8px 28px 28px', position: 'sticky', bottom: 0, background: T.bgEl }}>
              <button data-testid="waiter-modal-close" onClick={() => setShowWaiterModal(false)} style={{
                ...S.goldBtn, padding: '18px', fontSize: '20px',
              }}>
                {tr(language, 'waiter_modal_close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function GuidedFlow() {
  return (
    <GuidedFlowErrorBoundary>
      <GuidedFlowInner />
    </GuidedFlowErrorBoundary>
  );
}
